import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Plus, Search, Edit, Archive, ArchiveRestore, Trash2, ChevronLeft, ChevronRight, Package, X, RefreshCw } from 'lucide-react';
import EquipmentForm from '../EquipmentForm';
import SearchCounter from '../SearchCounter';
import BrandBadge from '../BrandBadge';
import { useAuth } from '../../contexts/AuthContext';
import { getAllEquipment, getEquipmentInUse, searchEquipmentLocal, refreshEquipmentData } from '../../lib/equipmentService';
import { collection, getDocs, query } from 'firebase/firestore';
import { matchesSearchTerm } from '../../lib/searchUtils';
import { useLocation, useNavigate } from 'react-router-dom';

const EquipmentCatalogTab = () => {
  const { userRole, showCacheRefreshButtons } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize search term from URL params
  const searchParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [showArchived, setShowArchived] = useState(searchParams.get('archived') === 'true');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [equipmentInUse, setEquipmentInUse] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Update URL when search term or archived state changes
  const updateUrlParams = (newSearchTerm, newShowArchived) => {
    const params = new URLSearchParams();
    if (newSearchTerm) {
      params.set('search', newSearchTerm);
    }
    if (newShowArchived) {
      params.set('archived', 'true');
    }
    
    const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname;
    navigate(newUrl, { replace: true });
  };

  useEffect(() => {
    fetchEquipment();
    fetchBrands();
  }, [showArchived]);

  const fetchBrands = async () => {
    try {
      // Use the same brands collection as other components to get user-selected colors
      const brandsQuery = query(collection(db, 'marcas'));
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      console.log('Brands collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use local-first equipment service
      const equipmentList = await getAllEquipment(showArchived);
      setEquipment(equipmentList);
      
      // Check which equipment is used in orders (optimized)
      await checkEquipmentUsage(equipmentList);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      console.log('Equipment catalog collection may not exist yet, starting with empty list');
      setEquipment([]);
      
      // Still check equipment usage even if fetching failed
      await checkEquipmentUsage([]);
    } finally {
      setLoading(false);
    }
  };

  const handleForceRefresh = async () => {
    try {
      setIsRefreshing(true);
      setError('');
      
      console.log('Force refreshing equipment and brands cache...');
      
      // Force refresh both equipment and brands from Firestore
      const [equipmentList] = await Promise.all([
        refreshEquipmentData(showArchived),
        fetchBrands() // Also refresh brands to get updated names
      ]);
      
      setEquipment(equipmentList);
      
      // Check which equipment is used in orders (optimized)
      await checkEquipmentUsage(equipmentList);
      
      setSuccess(`Cache actualizado: ${equipmentList.length} equipos cargados`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error force refreshing equipment:', error);
      setError('Error al actualizar la caché de equipos');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };


  const checkEquipmentUsage = async (equipmentList) => {
    try {
      // Use optimized batch usage check instead of individual queries
      const equipmentIds = equipmentList.map(item => item.id);
      const inUseSet = await getEquipmentInUse(equipmentIds);
      setEquipmentInUse(inUseSet);
    } catch (error) {
      console.error('Error checking equipment usage:', error);
    }
  };

  // Helper function to get brand object by ID or name
  const getBrandByNameOrId = (identifier) => {
    if (!identifier) {
      return { nombre: 'Sin marca', color: '#6B7280' };
    }
    
    // Since brands are now derived from equipment data with name as ID,
    // we can find by either ID or name (they're the same)
    const brand = brands.find(b => b.id === identifier || b.nombre === identifier);
    
    if (brand) {
      return brand;
    }
    
    // If no brand found, check if identifier looks like an ID (starts with brand_ or uuid)
    // If so, try to find a brand that has this identifier as name
    if (typeof identifier === 'string' && (identifier.startsWith('brand_') || identifier.length > 20)) {
      // This might be an old ID format, try to find by name matching
      const brandByName = brands.find(b => b.nombre === identifier);
      if (brandByName) {
        return brandByName;
      }
      // Return a clean name without the ID prefix
      const cleanName = identifier.startsWith('brand_') ? identifier.replace('brand_', '') : identifier;
      return { nombre: cleanName, color: '#6B7280' };
    }
    
    // Return found brand or create a fallback brand object with the identifier as name
    return { nombre: identifier, color: '#6B7280' };
  };

  const filteredEquipment = equipment.filter(item => {
    if (!searchTerm) return true;
    
    const searchTermLower = searchTerm.toLowerCase();
    
    // Get brand name dynamically for search
    const brand = getBrandByNameOrId(item.marcaId);
    const brandName = brand?.nombre || '';
    
    const equipmentFields = [
      brandName, // Use dynamically looked up brand name
      item.tipo,
      item.modelo,
      item.numeroParte,
      ...(item.partesAlternativas || []),
      ...(item.especificaciones ? Object.values(item.especificaciones) : [])
    ].filter(Boolean);
    
    return equipmentFields.some(field => 
      field && matchesSearchTerm(field.toString(), searchTerm)
    );
  });

  // Pagination calculations
  const totalItems = filteredEquipment.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredEquipment.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showArchived]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEdit = (equipmentItem) => {
    setEditingEquipment(equipmentItem);
    setShowForm(true);
  };


  const handleArchive = async (equipmentItem) => {
    if (!window.confirm(`¿Está seguro de archivar el equipo ${equipmentItem.modelo}?`)) return;

    try {
      await updateDoc(doc(db, 'equipment_catalog', equipmentItem.id), {
        isArchived: true,
        updatedAt: new Date()
      });
      setSuccess(`${equipmentItem.modelo} ha sido archivado`);
      
      try {
        // Force refresh both equipment and brands from Firestore to ensure everything is up to date
        const [equipmentList] = await Promise.all([
          refreshEquipmentData(showArchived),
          fetchBrands() // Also refresh brands to ensure consistency
        ]);
        
        setEquipment(equipmentList);
        
        // Check which equipment is used in orders (optimized)
        await checkEquipmentUsage(equipmentList);
      } catch (error) {
        console.error('Error refreshing equipment after archiving:', error);
        // Fallback to regular fetch if force refresh fails
        fetchEquipment();
        fetchBrands();
      }
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving equipment:', error);
      setError('Error al archivar el equipo');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUnarchive = async (equipmentItem) => {
    if (!window.confirm(`¿Está seguro de desarchivar el equipo ${equipmentItem.modelo}?`)) return;

    try {
      await updateDoc(doc(db, 'equipment_catalog', equipmentItem.id), {
        isArchived: false,
        updatedAt: new Date()
      });
      setSuccess(`${equipmentItem.modelo} ha sido desarchivado`);
      
      try {
        // Force refresh both equipment and brands from Firestore to ensure everything is up to date
        const [equipmentList] = await Promise.all([
          refreshEquipmentData(showArchived),
          fetchBrands() // Also refresh brands to ensure consistency
        ]);
        
        setEquipment(equipmentList);
        
        // Check which equipment is used in orders (optimized)
        await checkEquipmentUsage(equipmentList);
      } catch (error) {
        console.error('Error refreshing equipment after unarchiving:', error);
        // Fallback to regular fetch if force refresh fails
        fetchEquipment();
        fetchBrands();
      }
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error unarchiving equipment:', error);
      setError('Error al desarchivar el equipo');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (equipmentItem) => {
    try {
      // Check if equipment is used in any orders using the existing equipmentInUse state
      if (equipmentInUse.has(equipmentItem.id)) {
        setError(`No se puede eliminar ${equipmentItem.modelo} porque está siendo usado en una o más órdenes de trabajo. Debe archivar el equipo en su lugar.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente el equipo ${equipmentItem.modelo}?\n\n` +
        `Esta acción no se puede deshacer. Si desea mantener el historial, ` +
        `considere archivar el equipo en su lugar.`
      )) {
        return;
      }

      await deleteDoc(doc(db, 'equipment_catalog', equipmentItem.id));
      setSuccess(`${equipmentItem.modelo} ha sido eliminado permanentemente`);
      
      try {
        // Force refresh both equipment and brands from Firestore to ensure everything is up to date
        const [equipmentList] = await Promise.all([
          refreshEquipmentData(showArchived),
          fetchBrands() // Also refresh brands to ensure consistency
        ]);
        
        setEquipment(equipmentList);
        
        // Check which equipment is used in orders (optimized)
        await checkEquipmentUsage(equipmentList);
      } catch (error) {
        console.error('Error refreshing equipment after deletion:', error);
        // Fallback to regular fetch if force refresh fails
        fetchEquipment();
        fetchBrands();
      }
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting equipment:', error);
      setError('Error al eliminar el equipo');
      setTimeout(() => setError(''), 5000);
    }
  };

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const canPerformActions = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const handleFormSuccess = async () => {
    const message = editingEquipment ? 'Equipo actualizado exitosamente' : 'Equipo creado exitosamente';
    setSuccess(message);
    setEditingEquipment(null);
    
    try {
      // Force refresh both equipment and brands from Firestore to ensure everything is up to date
      const [equipmentList] = await Promise.all([
        refreshEquipmentData(showArchived),
        fetchBrands() // Also refresh brands in case new brands were added/updated
      ]);
      
      setEquipment(equipmentList);
      
      // Check which equipment is used in orders (optimized)
      await checkEquipmentUsage(equipmentList);
    } catch (error) {
      console.error('Error refreshing equipment after form success:', error);
      // Fallback to regular fetch if force refresh fails
      fetchEquipment();
      fetchBrands();
    }
    
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEquipment(null);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const showEllipsis = totalPages > 7;
    
    if (showEllipsis) {
      if (currentPage <= 3) {
        for (let i = 1; i <= Math.min(5, totalPages); i++) {
          pageNumbers.push(i);
        }
        if (totalPages > 5) {
          pageNumbers.push('...');
          pageNumbers.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    } else {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    }

    return (
      <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <button
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
              <span className="font-medium">{Math.min(endIndex, totalItems)}</span> de{' '}
              <span className="font-medium">{totalItems}</span> resultados
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pageNumbers.map((number, index) => (
                <React.Fragment key={index}>
                  {number === '...' ? (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0">
                      ...
                    </span>
                  ) : (
                    <button
                      onClick={() => handlePageChange(number)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 ${
                        currentPage === number
                          ? 'z-10 bg-purple-600 dark:bg-purple-700 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 dark:focus-visible:outline-purple-400'
                          : 'text-gray-900 dark:text-gray-300'
                      }`}
                    >
                      {number}
                    </button>
                  )}
                </React.Fragment>
              ))}
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Package className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400 flex-shrink-0" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Equipos</h2>
        </div>
        <div className="flex items-center space-x-2">
          {/* Force Refresh Button */}
          {showCacheRefreshButtons && (
            <button 
              onClick={handleForceRefresh}
              disabled={isRefreshing}
              className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Actualizar caché de equipos"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          
          {/* New Equipment Button */}
          {canEdit() && (
            <button 
              onClick={() => setShowForm(true)}
              className="flex items-center justify-center w-10 h-10 bg-purple-600 dark:bg-purple-700 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400"
              title="Nuevo Equipo"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Buscar equipos por marca, tipo, modelo, número de parte..."
                value={searchTerm}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSearchTerm(newValue);
                  updateUrlParams(newValue, showArchived);
                }}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    updateUrlParams('', showArchived);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <SearchCounter 
              count={filteredEquipment.length} 
              label="equipos" 
              isSearching={!!searchTerm} 
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showArchived"
              checked={showArchived}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowArchived(newValue);
                updateUrlParams(searchTerm, newValue);
              }}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="showArchived" className="ml-2 block text-sm text-gray-900 dark:text-white">
              Mostrar archivados
            </label>
          </div>
        </div>
      </div>

      {/* Equipment List */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando catálogo de equipos...
          </div>
        ) : filteredEquipment.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron equipos que coincidan con la búsqueda' : 'No hay equipos registrados en el catálogo'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {/* Desktop headers */}
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Marca
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Tipo
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Modelo
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Número de Parte
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                      Especificaciones
                    </th>
                    
                    {/* Mobile headers */}
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider md:hidden">
                      Equipo
                    </th>
                    
                    {canPerformActions() && (
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Acciones
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => {
                          // Pass current search state to equipment detail
                          const params = new URLSearchParams();
                          if (searchTerm) params.set('search', searchTerm);
                          if (showArchived) params.set('archived', 'true');
                          const returnUrl = params.toString() ? `/equipment?${params.toString()}` : '/equipment';
                          navigate(`/equipment/${item.id}?returnUrl=${encodeURIComponent(returnUrl)}`);
                        }}>
                      
                      {/* Desktop columns */}
                      <td className="px-3 sm:px-6 py-4 text-sm font-medium text-gray-900 dark:text-white hidden md:table-cell">
                        <div className="flex items-center space-x-2">
                          <BrandBadge 
                            brand={getBrandByNameOrId(item.marcaId)} 
                            size="sm" 
                            className="flex-shrink-0"
                          />
                        </div>
                        {item.isArchived && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 mt-1">
                            Archivado
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 dark:text-white hidden md:table-cell">
                        <div className="truncate" title={item.tipo}>
                          {item.tipo || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 dark:text-white hidden md:table-cell">
                        <div className="truncate font-medium" title={item.modelo}>
                          {item.modelo || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 dark:text-white hidden md:table-cell">
                        <div className="truncate" title={item.numeroParte}>
                          {item.numeroParte || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 dark:text-white hidden lg:table-cell">
                        <div className="text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                            {item.especificaciones ? Object.keys(item.especificaciones).length : 0}
                          </span>
                        </div>
                      </td>
                      
                      {/* Mobile "Equipo" column - contains all info */}
                      <td className="px-3 sm:px-6 py-4 text-sm md:hidden">
                        <div className="space-y-2">
                          {/* Brand and archived status */}
                          <div>
                            <div className="flex items-center space-x-2">
                              <BrandBadge 
                                brand={getBrandByNameOrId(item.marcaId)} 
                                size="md" 
                                className="flex-shrink-0"
                              />
                              {item.isArchived && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                  Archivado
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Tipo */}
                          <div className="text-xs">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Tipo:</span>
                            <span className="text-gray-900 dark:text-white ml-1">{item.tipo || 'N/A'}</span>
                          </div>
                          
                          {/* Modelo */}
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.modelo || 'Sin modelo'}
                          </div>
                          
                          {/* Número de parte */}
                          <div className="text-xs">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Número de parte:</span>
                            <span className="text-gray-900 dark:text-white ml-1">{item.numeroParte || 'Sin número'}</span>
                          </div>
                          
                          {/* Especificaciones count */}
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Especificaciones:</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                              {item.especificaciones ? Object.keys(item.especificaciones).length : 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      
                      {/* Actions column */}
                      {canPerformActions() && (
                        <td className="px-3 sm:px-6 py-4 text-sm font-medium">
                          <div className="flex flex-col space-y-1 md:flex-row md:items-center md:space-y-0 md:space-x-1" onClick={(e) => e.stopPropagation()}>
                            {canEdit() && (
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded flex justify-center"
                                title="Editar equipo"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {canEdit() && (
                              item.isArchived ? (
                                <button
                                  onClick={() => handleUnarchive(item)}
                                  className="p-2 text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded flex justify-center"
                                  title="Desarchivar equipo"
                                >
                                  <ArchiveRestore className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleArchive(item)}
                                  className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded flex justify-center"
                                  title="Archivar equipo"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                              )
                            )}
                            {canDelete() && (
                              <button
                                onClick={() => !equipmentInUse.has(item.id) && handleDelete(item)}
                                className={`p-2 rounded flex justify-center ${
                                  equipmentInUse.has(item.id)
                                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                }`}
                                title={
                                  equipmentInUse.has(item.id)
                                    ? 'No se puede eliminar: equipo usado en órdenes de trabajo'
                                    : 'Eliminar equipo'
                                }
                                disabled={equipmentInUse.has(item.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {renderPagination()}
          </>
        )}
      </div>

      {/* Equipment Form Modal */}
      <EquipmentForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingEquipment={editingEquipment}
      />
    </div>
  );
};

export default EquipmentCatalogTab;